import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarClock, Eye, KeyRound, Laptop, Pencil, Plus, Search, Trash2, User, Users, Wallet } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { StatCard } from '../../../components/StatCard';
import { EmptyState } from '../../../components/EmptyState';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { useLicenseStore, type LicenseInput, type SoftwareLicense } from '../../../store/licenseStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useAssetSettingsStore } from '../../../store/assetSettingsStore';
import { useAssetStore } from '../../../store/assetStore';
import { useEmployeeAssetsStore } from '../../../store/employeeAssetsStore';
import { useAssetGrant } from '../requests/requestUi';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';
const labelClass = 'mb-1 block text-xs font-medium text-muted-foreground';

const EMPTY: LicenseInput = {
  name: '', publisher: '', licenseKey: '', licenseType: '', totalSeats: '', purchaseDate: '', expiryDate: '',
  cost: '', currency: 'INR', supplierId: '', notes: '',
};

const toInput = (l: SoftwareLicense): LicenseInput => ({
  name: l.name, publisher: l.publisher || '', licenseKey: l.licenseKey || '', licenseType: l.licenseType || '',
  totalSeats: l.totalSeats || '', purchaseDate: l.purchaseDate || '', expiryDate: l.expiryDate || '',
  cost: l.cost ?? '', currency: l.currency || 'INR', supplierId: l.supplierId ?? '', notes: l.notes || '',
});

const money = (n: number, currency = 'INR') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${n.toLocaleString()}`; }
};

const today = () => new Date().toISOString().slice(0, 10);
const daysUntil = (d: string) => Math.round((new Date(`${d}T00:00:00`).getTime() - new Date(`${today()}T00:00:00`).getTime()) / 86_400_000);

type Expiry = 'expired' | 'expiring' | 'active' | 'none';
const expiryOf = (l: SoftwareLicense, alertDays: number): Expiry => {
  if (!l.expiryDate) return 'none';
  const d = daysUntil(l.expiryDate);
  return d < 0 ? 'expired' : d <= alertDays ? 'expiring' : 'active';
};

function ExpiryBadge({ license, alertDays }: { license: SoftwareLicense; alertDays: number }) {
  const state = expiryOf(license, alertDays);
  if (state === 'none') return <Badge variant="muted">No expiry</Badge>;
  const d = daysUntil(license.expiryDate!);
  if (state === 'expired') return <Badge variant="destructive">Expired {Math.abs(d)}d ago</Badge>;
  if (state === 'expiring') return <Badge variant="warning">{d === 0 ? 'Expires today' : `Expires in ${d}d`}</Badge>;
  return <Badge variant="success">Active</Badge>;
}

function SeatBar({ license }: { license: SoftwareLicense }) {
  if (license.totalSeats === 0) {
    return <span className="text-sm tabular-nums text-foreground">{license.seatsUsed} <span className="text-xs text-muted-foreground">/ unlimited</span></span>;
  }
  const pct = Math.min(100, Math.round((license.seatsUsed / license.totalSeats) * 100));
  const tone = pct >= 100 ? 'bg-destructive' : pct >= 85 ? 'bg-warning' : 'bg-primary';
  return (
    <div className="min-w-32">
      <div className="mb-1 flex justify-between text-xs tabular-nums">
        <span className="text-foreground">{license.seatsUsed} / {license.totalSeats}</span>
        <span className="text-muted-foreground">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-secondary"><div className={`h-1.5 rounded-full ${tone}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/** Software licence tracking — seats, renewals and who holds each seat. */
export function LicensesView() {
  const { licenses, types, loaded, error, fetchLicenses, fetchTypes, fetchLicense, createLicense, updateLicense, deleteLicense, assignSeat, releaseSeat } = useLicenseStore();
  const { vendors, fetchVendors } = useAssetCatalogStore();
  const { settings, fetchSettings } = useAssetSettingsStore();
  const { assets, fetchAssets } = useAssetStore();
  const { directory, fetchDirectory } = useEmployeeAssetsStore();
  const isManager = useAssetGrant('Licenses', 'Edit');

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | Expiry | 'full'>('all');
  const [editor, setEditor] = useState<{ id: string | null; form: LicenseInput } | null>(null);
  const [deleting, setDeleting] = useState<SoftwareLicense | null>(null);
  const [seats, setSeats] = useState<SoftwareLicense | null>(null);
  const [target, setTarget] = useState<{ kind: 'employee' | 'device'; id: string; notes: string }>({ kind: 'employee', id: '', notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchLicenses();
    fetchTypes();
    fetchVendors();
    fetchSettings();
  }, [fetchLicenses, fetchTypes, fetchVendors, fetchSettings]);

  // Employee and device pickers are only needed by managers.
  useEffect(() => {
    if (!isManager) return;
    fetchAssets();
    fetchDirectory().catch((e) => toast.error(e.message));
  }, [isManager, fetchAssets, fetchDirectory]);

  const alertDays = settings?.warranty.warranty_alert_threshold_days ?? 60;

  const kpi = useMemo(() => {
    const limited = licenses.filter((l) => l.totalSeats > 0);
    const costByCurrency = licenses.reduce<Record<string, number>>((acc, l) => {
      if (l.cost) acc[l.currency] = (acc[l.currency] || 0) + l.cost;
      return acc;
    }, {});
    return {
      total: licenses.length,
      used: licenses.reduce((n, l) => n + l.seatsUsed, 0),
      seats: limited.reduce((n, l) => n + l.totalSeats, 0),
      unlimited: licenses.length - limited.length,
      expiring: licenses.filter((l) => expiryOf(l, alertDays) === 'expiring').length,
      expired: licenses.filter((l) => expiryOf(l, alertDays) === 'expired').length,
      cost: Object.entries(costByCurrency).map(([c, n]) => money(n, c)).join(' + ') || money(0),
    };
  }, [licenses, alertDays]);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return licenses.filter((l) => {
      if (n && !`${l.name} ${l.publisher || ''} ${l.supplierName || ''}`.toLowerCase().includes(n)) return false;
      if (filter === 'all') return true;
      if (filter === 'full') return l.totalSeats > 0 && l.seatsUsed >= l.totalSeats;
      return expiryOf(l, alertDays) === filter;
    });
  }, [licenses, q, filter, alertDays]);

  const openSeats = async (l: SoftwareLicense) => {
    setSeats(l);
    setTarget({ kind: 'employee', id: '', notes: '' });
    try { setSeats(await fetchLicense(l.id)); } catch (e: any) { toast.error(e.message); }
  };

  const save = async () => {
    if (!editor) return;
    if (!editor.form.name.trim()) { toast.error('Software name is required'); return; }
    setBusy(true);
    try {
      if (editor.id) {
        await updateLicense(editor.id, editor.form);
        toast.success('Licence updated');
      } else {
        await createLicense(editor.form);
        toast.success('Licence added');
      }
      setEditor(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteLicense(deleting.id);
      toast.success(`${deleting.name} removed`);
      setDeleting(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!seats) return;
    if (!target.id) { toast.error(`Choose ${target.kind === 'employee' ? 'an employee' : 'a device'}`); return; }
    setBusy(true);
    try {
      const updated = await assignSeat(seats.id, target.kind === 'employee'
        ? { employeeId: target.id, notes: target.notes }
        : { deviceId: target.id, notes: target.notes });
      setSeats(updated);
      setTarget({ ...target, id: '', notes: '' });
      toast.success('Seat assigned');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const release = async (assignmentId: string) => {
    if (!seats) return;
    setBusy(true);
    try {
      setSeats(await releaseSeat(seats.id, assignmentId));
      toast.success('Seat released');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof LicenseInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    editor && setEditor({ ...editor, form: { ...editor.form, [k]: e.target.value } });

  const active = seats?.assignments?.filter((a) => !a.unassignedDate) ?? [];
  const past = seats?.assignments?.filter((a) => a.unassignedDate) ?? [];
  const heldBy = new Set(active.map((a) => a.userId).filter(Boolean));
  const seatsFull = !!seats && seats.totalSeats > 0 && active.length >= seats.totalSeats;
  const seatsExpired = !!seats?.expiryDate && seats.expiryDate < today();
  const devices = assets.filter((a) => a.status !== 'Retired' && a.status !== 'Disposed');

  const FILTERS: { key: typeof filter; label: string }[] = [
    { key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'expiring', label: 'Expiring soon' },
    { key: 'expired', label: 'Expired' }, { key: 'full', label: 'No free seats' },
  ];

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        className="mb-0"
        icon={KeyRound}
        title="Software licences"
        description="Track seats, renewals and who holds each licence."
        actions={isManager && <Button onClick={() => setEditor({ id: null, form: EMPTY })}><Plus /> Add licence</Button>}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Licences" value={kpi.total} icon={<KeyRound className="size-4" />} hint={kpi.unlimited ? `${kpi.unlimited} with unlimited seats` : undefined} />
        <StatCard label="Seats in use" value={kpi.seats ? `${kpi.used} / ${kpi.seats}` : kpi.used} icon={<Users className="size-4" />} hint={kpi.seats ? `${Math.max(0, kpi.seats - licenses.filter((l) => l.totalSeats > 0).reduce((n, l) => n + l.seatsUsed, 0))} seats free` : undefined} />
        <StatCard label="Renewals due" value={kpi.expiring} icon={<CalendarClock className="size-4" />} hint={`${kpi.expired} expired · alert window ${alertDays} days`} onClick={() => setFilter('expiring')} />
        <StatCard label="Licence spend" value={kpi.cost} icon={<Wallet className="size-4" />} hint="Total purchase cost recorded" />
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <Button key={f.key} size="sm" variant={filter === f.key ? 'secondary' : 'ghost'} onClick={() => setFilter(f.key)}>{f.label}</Button>
            ))}
          </div>
          <div className="w-full sm:w-72">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search software, publisher, vendor" leftIcon={<Search className="size-4 text-muted-foreground" />} />
          </div>
        </div>
        {error ? (
          <EmptyState className="m-4 border-0" icon={<AlertTriangle />} title="Could not load licences" description={error} />
        ) : rows.length === 0 ? (
          <EmptyState
            className="m-4 border-0"
            title={!loaded ? 'Loading…' : licenses.length ? 'No matching licences' : 'No licences yet'}
            description={licenses.length ? 'Try a different search or filter.' : 'Add the software subscriptions and licences your company pays for.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Software</th>
                  <th className="px-4 py-2 font-semibold">Type</th>
                  <th className="px-4 py-2 font-semibold">Seats</th>
                  <th className="px-4 py-2 font-semibold">Expiry</th>
                  <th className="px-4 py-2 text-right font-semibold">Cost</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((l) => (
                  <tr key={l.id} className="hover:bg-secondary/30">
                    <td className="px-4 py-2">
                      <button type="button" className="text-left font-medium text-foreground hover:text-primary" onClick={() => openSeats(l)}>{l.name}</button>
                      <p className="text-xs text-muted-foreground">{[l.publisher, l.supplierName && `via ${l.supplierName}`].filter(Boolean).join(' · ') || '—'}</p>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{l.licenseType || '—'}</td>
                    <td className="px-4 py-2"><SeatBar license={l} /></td>
                    <td className="px-4 py-2">
                      <ExpiryBadge license={l} alertDays={alertDays} />
                      {l.expiryDate && <p className="mt-0.5 text-xs text-muted-foreground">{l.expiryDate}</p>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{l.cost !== null ? money(l.cost, l.currency) : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openSeats(l)}>{isManager ? <><Users /> Seats</> : <><Eye /> View</>}</Button>
                      {isManager && (
                        <>
                          <Button size="icon-sm" variant="ghost" aria-label={`Edit ${l.name}`} onClick={() => setEditor({ id: l.id, form: toInput(l) })}><Pencil /></Button>
                          <Button size="icon-sm" variant="ghost" aria-label={`Delete ${l.name}`} onClick={() => setDeleting(l)}><Trash2 /></Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add / edit */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent side="right" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Edit licence' : 'Add licence'}</DialogTitle>
            <DialogDescription>Leave seats empty or 0 for site and enterprise licences with unlimited seats.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="sm:col-span-2"><span className={labelClass}>Software name *</span>
                <input className={fieldClass} value={editor.form.name} onChange={set('name')} maxLength={200} placeholder="e.g. Microsoft 365 Business" /></label>
              <label><span className={labelClass}>Publisher</span>
                <input className={fieldClass} value={editor.form.publisher} onChange={set('publisher')} maxLength={200} /></label>
              <label><span className={labelClass}>Licence type</span>
                <select className={fieldClass} value={editor.form.licenseType} onChange={set('licenseType')}>
                  <option value="">Not specified</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></label>
              <label className="sm:col-span-2"><span className={labelClass}>Licence key</span>
                <input className={`${fieldClass} font-mono`} value={editor.form.licenseKey} onChange={set('licenseKey')} maxLength={500} autoComplete="off" /></label>
              <label><span className={labelClass}>Total seats</span>
                <input className={fieldClass} type="number" min={0} value={editor.form.totalSeats} onChange={set('totalSeats')} placeholder="0 = unlimited" /></label>
              <label><span className={labelClass}>Vendor</span>
                <select className={fieldClass} value={String(editor.form.supplierId ?? '')} onChange={set('supplierId')}>
                  <option value="">None</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select></label>
              <label><span className={labelClass}>Purchase date</span>
                <input className={fieldClass} type="date" value={editor.form.purchaseDate} onChange={set('purchaseDate')} /></label>
              <label><span className={labelClass}>Expiry / renewal date</span>
                <input className={fieldClass} type="date" value={editor.form.expiryDate} onChange={set('expiryDate')} min={editor.form.purchaseDate || undefined} /></label>
              <label><span className={labelClass}>Cost</span>
                <input className={fieldClass} type="number" min={0} step="0.01" value={editor.form.cost} onChange={set('cost')} /></label>
              <label><span className={labelClass}>Currency</span>
                <input className={`${fieldClass} uppercase`} value={editor.form.currency} onChange={set('currency')} maxLength={3} /></label>
              <label className="sm:col-span-2"><span className={labelClass}>Notes</span>
                <textarea className="min-h-16 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" value={editor.form.notes} onChange={set('notes')} maxLength={2000} /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditor(null)} disabled={busy}>Cancel</Button>
            <Button onClick={save} isLoading={busy}>{editor?.id ? 'Save changes' : 'Add licence'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seats */}
      <Dialog open={!!seats} onOpenChange={(o) => !o && setSeats(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{seats?.name}</DialogTitle>
            <DialogDescription>
              {seats && (seats.totalSeats === 0 ? `${active.length} seats in use · unlimited` : `${active.length} of ${seats.totalSeats} seats in use`)}
              {seats?.licenseKey && <span className="ml-2 font-mono">· {seats.licenseKey}</span>}
            </DialogDescription>
          </DialogHeader>

          {isManager && seats && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3">
              {seatsExpired ? (
                <p className="text-sm text-destructive-strong">This licence has expired. Update the expiry date to assign seats.</p>
              ) : seatsFull ? (
                <p className="text-sm text-warning-strong">All seats are in use. Release a seat or increase the total.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-[8rem_minmax(0,1fr)_auto]">
                  <select className={fieldClass} value={target.kind} onChange={(e) => setTarget({ kind: e.target.value as 'employee' | 'device', id: '', notes: target.notes })}>
                    <option value="employee">Employee</option>
                    <option value="device">Device</option>
                  </select>
                  {target.kind === 'employee' ? (
                    <select className={fieldClass} value={target.id} onChange={(e) => setTarget({ ...target, id: e.target.value })}>
                      <option value="">Choose employee…</option>
                      {directory.map((e) => (
                        <option key={e.id} value={e.id} disabled={!e.userId || (!!e.userId && heldBy.has(e.userId))}>
                          {e.name}{e.code ? ` (${e.code})` : ''}{e.department ? ` — ${e.department}` : ''}{!e.userId ? ' · no account' : heldBy.has(e.userId) ? ' · has a seat' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select className={fieldClass} value={target.id} onChange={(e) => setTarget({ ...target, id: e.target.value })}>
                      <option value="">Choose device…</option>
                      {devices.map((a) => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}
                    </select>
                  )}
                  <Button onClick={assign} isLoading={busy}>Assign seat</Button>
                </div>
              )}
              {target.kind === 'employee' && heldBy.size > 0 && !seatsFull && !seatsExpired && (
                <p className="mt-2 text-xs text-muted-foreground">Employees who already hold a seat cannot be assigned twice.</p>
              )}
            </div>
          )}

          <div className="max-h-80 overflow-auto">
            {!seats?.assignments ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : active.length === 0 && past.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No seats assigned yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr><th className="py-2 font-semibold">Holder</th><th className="py-2 font-semibold">Assigned</th><th className="py-2 font-semibold">Status</th><th /></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[...active, ...past].map((a) => (
                    <tr key={a.id}>
                      <td className="py-2">
                        {a.userId && (
                          <p className="flex items-center gap-1.5 text-foreground"><User className="size-3.5 text-muted-foreground" /> {a.userName || a.userEmail || 'Unknown user'}
                            {a.userDepartment && <span className="text-xs text-muted-foreground">· {a.userDepartment}</span>}</p>
                        )}
                        {a.deviceId && (
                          <p className="flex items-center gap-1.5 text-foreground"><Laptop className="size-3.5 text-muted-foreground" /> {a.deviceCode} <span className="text-xs text-muted-foreground">{a.deviceName}</span></p>
                        )}
                      </td>
                      <td className="py-2 tabular-nums text-muted-foreground">{a.assignedDate}</td>
                      <td className="py-2">{a.unassignedDate ? <Badge variant="muted">Released {a.unassignedDate}</Badge> : <Badge variant="success">Active</Badge>}</td>
                      <td className="py-2 text-right">
                        {isManager && !a.unassignedDate && <Button size="sm" variant="ghost" onClick={() => release(a.id)} disabled={busy}>Release</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSeats(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting?.seatsUsed
                ? `${deleting.seatsUsed} active seat(s) will be released. Seat history is kept in the audit log.`
                : 'The licence will be removed from tracking.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} isLoading={busy}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
